import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Bottone } from '../../componenti/base.tsx'
import { IconaBandierina, IconaEtichetta } from '../../componenti/Icone.tsx'
import * as posta from '../../servizi/posta.ts'
import '../../stile-posta-segni.css'

/**
 * Bandierine ed etichette delle conversazioni (538; richiesta di Carmem riportata da Davide il 2/11/2026).
 *
 * - Nell'elenco la riga con una bandierina ha la barra del colore a sinistra, il fondo tinto e l'icona della
 *   bandierina con il nome del colore (il colore da solo non basta: chi non lo distingue legge il nome).
 *   Con la bandierina per tutti e la tua insieme, due barre e due icone.
 * - Il menu «Bandierina ed etichette» (nella conversazione aperta e su ogni riga dell'elenco) mette e toglie le
 *   bandierine, mette e toglie le etichette, ne crea, le rinomina e le elimina. Quelle per tutti con l'accesso
 *   pieno alla casella; le tue sempre, tranne impersonando (524), dove il menu non c'e'. Sul telefono si apre
 *   come un foglio dal basso.
 * - I filtri sopra l'elenco: per bandierina e per etichetta, in tutte le cartelle della casella.
 */

export function testoBandierina(colore: posta.Colore, per: posta.Per, messaDa?: { nome: string; cognome: string } | null) {
  return `Bandierina ${posta.nomeColore(colore)}, ${per === 'me' ? 'solo per te' : 'per tutti'}`
    + (messaDa ? `, messa da ${messaDa.nome} ${messaDa.cognome}` : '')
}

/** Una bandierina: icona piena nel colore, con il nome del colore come etichetta accessibile e suggerimento. */
export function Bandierina({ colore, per, messaDa }: {
  colore: posta.Colore; per: posta.Per; messaDa?: { nome: string; cognome: string } | null
}) {
  const testo = testoBandierina(colore, per, messaDa)
  return (
    <span className={`segni-bandierina segni-c-${colore}`} role="img" aria-label={testo} title={testo}>
      <IconaBandierina piena />
    </span>
  )
}

/** Le bandierine di una conversazione: prima quella per tutti, poi la tua. */
export function Bandierine({ segni }: { segni: posta.SegniThread }) {
  if (!segni.bandierina && !segni.bandierina_personale) return null
  return (
    <span className="segni-bandierine">
      {segni.bandierina && <Bandierina colore={segni.bandierina.colore} per="tutti" messaDa={segni.bandierina.messa_da} />}
      {segni.bandierina_personale && <Bandierina colore={segni.bandierina_personale} per="me" />}
    </span>
  )
}

/** Le etichette di una conversazione. Nell'elenco al massimo `massimo`, poi «+N». */
export function Etichette({ etichette, massimo }: { etichette: posta.EtichettaSuThread[]; massimo?: number }) {
  if (!etichette.length) return null
  const mostrate = massimo ? etichette.slice(0, massimo) : etichette
  const altre = etichette.length - mostrate.length
  return (
    <span className="segni-etichette">
      {mostrate.map((e) => (
        <span key={e.id} className={`segni-etichetta${e.per === 'me' ? ' personale' : ''}`}
              title={e.per === 'me' ? `Etichetta «${e.nome}», solo per te` : `Etichetta «${e.nome}», per tutti`}>
          <IconaEtichetta />
          <span className="segni-etichetta-nome">{e.nome}</span>
          {e.per === 'me' && <span className="solo-lettore">, solo per te</span>}
        </span>
      ))}
      {altre > 0 && <span className="segni-etichetta segni-altre" title={etichette.slice(massimo).map((e) => e.nome).join(', ')}>+{altre}</span>}
    </span>
  )
}

/** Gli attributi della riga dell'elenco che danno barra e tinta (stile-posta-segni.css). */
export function attributiRiga(segni: posta.SegniThread) {
  return {
    'data-bandierina': segni.bandierina?.colore,
    'data-bandierina-personale': segni.bandierina_personale ?? undefined,
  }
}

// --- filtri sopra l'elenco ---------------------------------------------------------------------------

export function FiltroBandierina({ valore, cambia }: {
  valore: posta.FiltroBandierina | ''; cambia: (v: posta.FiltroBandierina | '') => void
}) {
  return (
    <select value={valore} aria-label="Filtra per bandierina" className={`posta-filtro-segni${valore ? ' posta-filtro-attivo' : ''}`}
            onChange={(e) => cambia(e.target.value as posta.FiltroBandierina | '')}>
      <option value="">Bandierina: tutte</option>
      <option value="qualsiasi">Con bandierina</option>
      {posta.COLORI.map((c) => <option key={c.valore} value={c.valore}>Bandierina {c.nome}</option>)}
    </select>
  )
}

/**
 * Il filtro per etichetta: per tutti e tue. Nella vista «Tutte le caselle» quelle per tutti stanno sotto la
 * casella a cui appartengono. Senza etichette non si mostra.
 */
export function FiltroEtichetta({ valore, cambia, etichette, indirizzoDi, vistaUnica }: {
  valore: string; cambia: (v: string) => void; etichette: posta.EtichettaPosta[]
  indirizzoDi: Map<string, string>; vistaUnica: boolean
}) {
  if (!etichette.length) return null
  const perTutti = etichette.filter((e) => e.per === 'tutti')
  const tue = etichette.filter((e) => e.per === 'me')
  const gruppi: { titolo: string; voci: posta.EtichettaPosta[] }[] = []
  if (vistaUnica) {
    for (const casella of [...new Set(perTutti.map((e) => e.casella_id!))]) {
      gruppi.push({ titolo: `Per tutti · ${indirizzoDi.get(casella) ?? 'casella'}`, voci: perTutti.filter((e) => e.casella_id === casella) })
    }
  } else if (perTutti.length) gruppi.push({ titolo: 'Per tutti', voci: perTutti })
  if (tue.length) gruppi.push({ titolo: 'Solo per te', voci: tue })
  return (
    <select value={valore} aria-label="Filtra per etichetta" className={`posta-filtro-segni${valore ? ' posta-filtro-attivo' : ''}`}
            onChange={(e) => cambia(e.target.value)}>
      <option value="">Etichetta: tutte</option>
      {gruppi.map((g) => (
        <optgroup key={g.titolo} label={g.titolo}>
          {g.voci.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </optgroup>
      ))}
    </select>
  )
}

// --- menu ----------------------------------------------------------------------------------------------

/** Che cosa e' cambiato fra le etichette: la pagina rilegge l'elenco e aggiorna le conversazioni mostrate. */
export type CambioEtichette =
  | { tipo: 'creata'; etichetta: posta.EtichettaPosta }
  | { tipo: 'rinominata'; id: string; nome: string }
  | { tipo: 'eliminata'; id: string }

const LARGHEZZA_MENU = 340
const ALTEZZA_MENU = 560
/** Sotto questa larghezza il menu e' un foglio dal basso, a tutta larghezza. */
const LARGHEZZA_FOGLIO = 700

/** Dove sta il pannello sullo schermo: le coordinate cambiano con il comando, e vanno nello stile. */
type Posto = { left: number; top?: number; bottom?: number; maxHeight: number }

const testoErrore = (e: unknown) => (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: riprova.')

export function MenuSegni({ thread, casella, etichette, cambiati, etichetteCambiate, nellElenco = false }: {
  thread: { id: string; casella_id: string; oggetto: string } & posta.SegniThread
  /** La casella della conversazione: con l'accesso pieno (`mia`) si gestiscono anche quelle per tutti. */
  casella: posta.CasellaWebmail | null
  /** Le etichette della vista: si usano quelle per tutti di questa casella e le tue. */
  etichette: posta.EtichettaPosta[]
  cambiati: (segni: posta.SegniThread) => void
  etichetteCambiate: (cambio: CambioEtichette) => void
  /** Sulla riga dell'elenco: il solo comando con l'icona. */
  nellElenco?: boolean
}) {
  const { io } = useSessione()
  const [aperto, setAperto] = useState(false)
  const ancora = useRef<HTMLSpanElement>(null)
  const idPannello = useId()

  const chiudi = useCallback((fuoco: boolean) => {
    setAperto(false)
    if (fuoco) ancora.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [])

  // Impersonando (524) non si scrive niente: il menu non c'e'.
  if (io?.impersonazione) return null

  const etichetta = `Bandierina ed etichette di «${thread.oggetto || 'senza oggetto'}»`
  return (
    <>
      <span ref={ancora} className={nellElenco ? 'segni-ancora-voce' : 'segni-ancora'}>
        {nellElenco
          ? (
            <button type="button" className="segni-comando-voce" aria-label={etichetta} title="Bandierina ed etichette"
                    aria-expanded={aperto} aria-controls={aperto ? idPannello : undefined}
                    onClick={() => setAperto((a) => !a)}>
              <IconaBandierina />
            </button>
          )
          : (
            <Bottone variante="discreto" dimensione="piccolo" icona={<IconaBandierina />}
                     aria-expanded={aperto} aria-controls={aperto ? idPannello : undefined}
                     onClick={() => setAperto((a) => !a)}>
              Bandierina ed etichette
            </Bottone>
          )}
      </span>
      {aperto && (
        <Pannello id={idPannello} ancora={ancora} chiudi={chiudi} titolo={etichetta}>
          <ContenutoMenu thread={thread} pieno={!!casella?.mia}
                         etichette={etichette.filter((e) => e.per === 'me' || e.casella_id === thread.casella_id)}
                         cambiati={cambiati} etichetteCambiate={etichetteCambiate} chiudi={() => chiudi(true)} />
        </Pannello>
      )}
    </>
  )
}

/**
 * Il pannello sopra la pagina, fuori dalle colonne che scorrono (che lo taglierebbero): sotto il comando,
 * o sopra se sotto non c'e' posto; sul telefono un foglio dal basso. Si chiude con Esc (il fuoco torna al
 * comando), toccando fuori o scorrendo la pagina sotto.
 */
function Pannello({ id, ancora, chiudi, titolo, children }: {
  id: string; ancora: RefObject<HTMLSpanElement | null>; chiudi: (fuoco: boolean) => void; titolo: string
  children: ReactNode
}) {
  const pannello = useRef<HTMLDivElement>(null)
  const [posto, setPosto] = useState<Posto | 'foglio' | null>(null)

  /**
   * Sotto il comando se ci sta, altrimenti sopra (ancorato in basso, cosi' cresce verso l'alto e non copre il
   * comando); se non ci sta da nessuna parte, dalla parte piu' larga e con lo scorrimento.
   */
  const misura = useCallback(() => {
    const a = ancora.current?.getBoundingClientRect()
    const p = pannello.current
    if (!a || !p) return
    if (window.innerWidth < LARGHEZZA_FOGLIO) { setPosto('foglio'); return }
    const alto = Math.min(p.scrollHeight, ALTEZZA_MENU)
    const left = Math.min(Math.max(8, a.right - LARGHEZZA_MENU), window.innerWidth - LARGHEZZA_MENU - 8)
    const sotto = window.innerHeight - a.bottom - 12
    const sopra = a.top - 12
    const giu = alto <= sotto || (alto > sopra && sotto >= sopra)
    // Dentro la finestra anche se il comando ne esce in parte (la riga in fondo alla colonna).
    const h = window.innerHeight
    const top = Math.max(8, Math.min(a.bottom + 4, h - 168))
    const bottom = Math.max(8, Math.min(h - a.top + 4, h - 168))
    const nuovo: Posto = giu
      ? { left, top, maxHeight: Math.min(ALTEZZA_MENU, h - top - 8) }
      : { left, bottom, maxHeight: Math.min(ALTEZZA_MENU, h - bottom - 8) }
    setPosto((prima) => (prima && prima !== 'foglio' && JSON.stringify(prima) === JSON.stringify(nuovo) ? prima : nuovo))
  }, [ancora])

  useLayoutEffect(() => { misura() }, [misura])
  // Il contenuto cambia (etichetta nuova, «Rinomina», conferma): si rimisura.
  useEffect(() => {
    const contenuto = pannello.current?.firstElementChild
    if (!contenuto) return
    const osserva = new ResizeObserver(() => misura())
    osserva.observe(contenuto)
    return () => osserva.disconnect()
  }, [misura])

  // Il fuoco entra nel pannello all'apertura, quando e' al suo posto: prima della misura e' invisibile, e un
  // elemento invisibile non prende il fuoco.
  const fuocoDato = useRef(false)
  useEffect(() => {
    if (!posto || fuocoDato.current) return
    fuocoDato.current = true
    pannello.current?.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled])')?.focus()
  }, [posto])

  useEffect(() => {
    const tasto = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); chiudi(true) } }
    const fuori = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node
      if (!pannello.current?.contains(t) && !ancora.current?.contains(t)) chiudi(false)
    }
    const scorre = (e: Event) => {
      if (window.innerWidth < LARGHEZZA_FOGLIO) return
      if (!pannello.current?.contains(e.target as Node)) chiudi(false)
    }
    document.addEventListener('keydown', tasto)
    document.addEventListener('mousedown', fuori)
    document.addEventListener('touchstart', fuori)
    window.addEventListener('scroll', scorre, true)
    window.addEventListener('resize', misura)
    return () => {
      document.removeEventListener('keydown', tasto)
      document.removeEventListener('mousedown', fuori)
      document.removeEventListener('touchstart', fuori)
      window.removeEventListener('scroll', scorre, true)
      window.removeEventListener('resize', misura)
    }
  }, [chiudi, ancora, misura])

  const foglio = posto === 'foglio'
  return createPortal(
    <>
      {foglio && <div className="segni-velo" aria-hidden="true" />}
      <div ref={pannello} id={id} role="dialog" aria-label={titolo}
           className={`segni-menu${foglio ? ' segni-foglio' : ''}${posto ? '' : ' segni-in-misura'}`}
           style={posto && posto !== 'foglio' ? posto : undefined}>
        {children}
      </div>
    </>,
    document.body,
  )
}

function ContenutoMenu({ thread, pieno, etichette, cambiati, etichetteCambiate, chiudi }: {
  thread: { id: string; casella_id: string } & posta.SegniThread
  pieno: boolean
  etichette: posta.EtichettaPosta[]
  cambiati: (segni: posta.SegniThread) => void
  etichetteCambiate: (cambio: CambioEtichette) => void
  chiudi: () => void
}) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [nome, setNome] = useState('')
  const [per, setPer] = useState<posta.Per>('me')
  const [inModifica, setInModifica] = useState<{ id: string; nome: string } | null>(null)
  const [daEliminare, setDaEliminare] = useState<posta.EtichettaPosta | null>(null)
  const idNome = useId()
  const su = new Set(thread.etichette.map((e) => e.id))
  const perTutti = etichette.filter((e) => e.per === 'tutti')
  const tue = etichette.filter((e) => e.per === 'me')

  async function esegui(f: () => Promise<void>) {
    setInCorso(true); setErrore(null)
    try { await f() } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  const bandierina = (p: posta.Per, colore: posta.Colore | null) =>
    esegui(async () => cambiati(await posta.mettiBandierina(thread.id, p, colore)))

  const alterna = (e: posta.EtichettaPosta, messa: boolean) =>
    esegui(async () => cambiati(await posta.mettiEtichetta(thread.id, e.id, messa)))

  function crea(ev: FormEvent) {
    ev.preventDefault()
    const pulito = nome.replace(/\s+/g, ' ').trim()
    if (!pulito) { setErrore('Scrivi il nome dell’etichetta.'); return }
    void esegui(async () => {
      const nuova = await posta.creaEtichetta(pulito, pieno ? per : 'me', thread.casella_id)
      etichetteCambiate({ tipo: 'creata', etichetta: nuova })
      // Creata dal menu di una conversazione: si mette subito su quella.
      cambiati(await posta.mettiEtichetta(thread.id, nuova.id, true))
      setNome('')
    })
  }

  function rinomina(ev: FormEvent) {
    ev.preventDefault()
    if (!inModifica) return
    const { id, nome: nuovo } = inModifica
    void esegui(async () => {
      const r = await posta.rinominaEtichetta(id, nuovo)
      etichetteCambiate({ tipo: 'rinominata', id, nome: r.nome })
      setInModifica(null)
    })
  }

  function elimina(e: posta.EtichettaPosta) {
    void esegui(async () => {
      await posta.eliminaEtichetta(e.id)
      etichetteCambiate({ tipo: 'eliminata', id: e.id })
      setDaEliminare(null)
    })
  }

  function voce(e: posta.EtichettaPosta) {
    const gestisce = e.per === 'me' || pieno
    if (inModifica?.id === e.id) {
      return (
        <li key={e.id} className="segni-voce">
          <form className="segni-rinomina" onSubmit={rinomina}>
            <input value={inModifica.nome} maxLength={posta.NOME_ETICHETTA_MASSIMO} aria-label={`Nuovo nome di «${e.nome}»`}
                   onChange={(x) => setInModifica({ id: e.id, nome: x.target.value })} autoFocus />
            <Bottone tipo="submit" dimensione="piccolo" variante="primario" inCorso={inCorso}>Salva</Bottone>
            <Bottone dimensione="piccolo" variante="discreto" onClick={() => setInModifica(null)}>Annulla</Bottone>
          </form>
        </li>
      )
    }
    if (daEliminare?.id === e.id) {
      return (
        <li key={e.id} className="segni-voce segni-conferma" role="group" aria-label={`Elimina «${e.nome}»`}>
          <p>
            Eliminare «{e.nome}»? La togli da tutte le conversazioni
            {e.per === 'tutti' ? ' della casella, per tutti quelli che la usano.' : ' in cui l’hai messa.'}
          </p>
          <span className="segni-azioni">
            <Bottone dimensione="piccolo" variante="critico" inCorso={inCorso} onClick={() => elimina(e)}>Elimina</Bottone>
            <Bottone dimensione="piccolo" variante="discreto" onClick={() => setDaEliminare(null)}>Annulla</Bottone>
          </span>
        </li>
      )
    }
    return (
      <li key={e.id} className="segni-voce">
        <label className="segni-spunta">
          <input type="checkbox" checked={su.has(e.id)} disabled={inCorso || !gestisce}
                 onChange={(x) => void alterna(e, x.target.checked)} />
          <span className="segni-etichetta-nome">{e.nome}</span>
        </label>
        {gestisce && (
          <span className="segni-azioni">
            <button type="button" className="segni-azione" disabled={inCorso}
                    onClick={() => { setDaEliminare(null); setInModifica({ id: e.id, nome: e.nome }) }}
                    aria-label={`Rinomina «${e.nome}»`}>Rinomina</button>
            <button type="button" className="segni-azione" disabled={inCorso}
                    onClick={() => { setInModifica(null); setDaEliminare(e) }}
                    aria-label={`Elimina «${e.nome}»`}>Elimina</button>
          </span>
        )}
      </li>
    )
  }

  return (
    <div className="segni-contenuto">
      <div className="segni-capo">
        <strong>Bandierina</strong>
        <button type="button" className="segni-azione" onClick={chiudi}>Chiudi</button>
      </div>
      <Colori titolo="Per tutti" nota={pieno ? 'la vedono tutti quelli che usano la casella'
                                         : 'la mette chi ha l’accesso pieno alla casella'}
              scelto={thread.bandierina?.colore ?? null} disabilitata={!pieno || inCorso}
              scegli={(c) => void bandierina('tutti', c)} />
      <Colori titolo="Solo per te" nota="la vedi solo tu" scelto={thread.bandierina_personale}
              disabilitata={inCorso} scegli={(c) => void bandierina('me', c)} />

      <div className="segni-capo"><strong>Etichette</strong></div>
      {!etichette.length && <p className="segni-nota">Nessuna etichetta: creane una qui sotto.</p>}
      {perTutti.length > 0 && (
        <>
          <p className="segni-sottotitolo">Per tutti{pieno ? '' : ' (le mette chi ha l’accesso pieno)'}</p>
          <ul className="segni-elenco">{perTutti.map(voce)}</ul>
        </>
      )}
      {tue.length > 0 && (
        <>
          <p className="segni-sottotitolo">Solo per te</p>
          <ul className="segni-elenco">{tue.map(voce)}</ul>
        </>
      )}

      <form className="segni-nuova" onSubmit={crea}>
        <label htmlFor={idNome}>Nuova etichetta</label>
        <div className="segni-nuova-riga">
          <input id={idNome} value={nome} maxLength={posta.NOME_ETICHETTA_MASSIMO} onChange={(e) => setNome(e.target.value)}
                 placeholder="per esempio: Da richiamare" />
          <Bottone tipo="submit" dimensione="piccolo" inCorso={inCorso}>Crea e metti</Bottone>
        </div>
        {pieno && (
          <fieldset className="segni-per">
            <legend className="solo-lettore">Chi la vede</legend>
            <label><input type="radio" name={`${idNome}-per`} checked={per === 'me'} onChange={() => setPer('me')} /> Solo per me</label>
            <label><input type="radio" name={`${idNome}-per`} checked={per === 'tutti'} onChange={() => setPer('tutti')} /> Per tutti quelli che usano la casella</label>
          </fieldset>
        )}
      </form>
      {errore && <p className="segni-errore" role="alert">{errore}</p>}
      <p className="segni-nota">Bandierine ed etichette restano nel gestionale: i programmi di posta collegati non le vedono.</p>
    </div>
  )
}

function Colori({ titolo, nota, scelto, disabilitata, scegli }: {
  titolo: string; nota: string; scelto: posta.Colore | null; disabilitata: boolean
  scegli: (c: posta.Colore | null) => void
}) {
  return (
    <div className="segni-colori" role="group" aria-label={`Bandierina ${titolo.toLowerCase()}`}>
      <span className="segni-colori-titolo">{titolo}<span className="segni-nota-breve">{nota}</span></span>
      <span className="segni-colori-scelte">
        {posta.COLORI.map((c) => (
          <button key={c.valore} type="button" className={`segni-colore segni-c-${c.valore}`} disabled={disabilitata}
                  aria-pressed={scelto === c.valore} aria-label={`Bandierina ${c.nome}`} title={`Bandierina ${c.nome}`}
                  onClick={() => scegli(scelto === c.valore ? null : c.valore)}>
            <IconaBandierina piena />
          </button>
        ))}
        <button type="button" className="segni-azione segni-nessuna" disabled={disabilitata || scelto === null}
                onClick={() => scegli(null)}>Togli</button>
      </span>
    </div>
  )
}
